<script lang="ts">
  import { contactById, mutedCategories } from '$lib/db/contacts';
  import { db, type ContactRow } from '$lib/db/db';
  import {
    eventRowsFor,
    markRead,
    mergeConversation,
    outboxRowsFor,
    type ConversationItem,
  } from '$lib/db/threads';
  import { formatPhone } from '$lib/format';
  import { t } from '$lib/i18n/index.svelte';
  import { prepareImage } from '$lib/media/image';
  import type { VoiceRecording } from '$lib/media/recorder';
  import { runHeldLoop } from '$lib/protocol/events-loop';
  import {
    discardOutboxItem,
    queueCartResponse,
    queueMessage,
    retryOutboxItem,
  } from '$lib/protocol/outbox';
  import {
    answerElementJson,
    requestElementJson,
    imageElementJson,
    textElementJson,
    voiceElementJson,
    type CalendarEventElement,
    type CartElement,
  } from '$lib/protocol/types';
  import { live } from '$lib/state/live';
  import { consumeFreshAdd } from '$lib/state/push-nudge';
  import { threadState } from '$lib/state/thread-state.svelte';

  import { goBack, navigate } from '../../app/router.svelte';
  import type { ElementContext } from '../elements/context';
  import ActivityStrip from '../parts/ActivityStrip.svelte';
  import Banner from '../parts/Banner.svelte';
  import ContactMenu from '../parts/ContactMenu.svelte';
  import ComposeBar from '../parts/ComposeBar.svelte';
  import MessageBubble from '../parts/MessageBubble.svelte';
  import PushNudge from '../parts/PushNudge.svelte';

  let { accountId }: { accountId: string } = $props();

  // The moment of highest intent: the conversation entered right after adding
  // this company shows the notification nudge once.
  const freshAdd = consumeFreshAdd();

  const contact = live<ContactRow | undefined>(() => contactById(accountId), undefined);
  const items = live<ConversationItem[]>(async () => {
    const [events, waiting] = await Promise.all([eventRowsFor(accountId), outboxRowsFor(accountId)]);
    return mergeConversation(events, waiting);
  }, []);
  const muted = live<Set<string>>(() => mutedCategories(accountId), new Set<string>());

  let listElement = $state<HTMLElement | null>(null);
  let menuOpen = $state(false);

  // The held loop is owned by the open conversation: one plain pass, then a
  // question outstanding at all times. Hidden tab ⇒ the request is aborted — a
  // phone in a pocket holding a request open would cost battery and a server
  // slot and buy nothing.
  $effect(() => {
    let controller: AbortController | null = null;
    const start = (): void => {
      if (controller === null) {
        controller = new AbortController();
        void runHeldLoop(accountId, controller.signal);
      }
    };
    const stop = (): void => {
      controller?.abort();
      controller = null;
    };
    const onVisibility = (): void => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    start();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  });

  // Opening the thread is the read signal — the only one that does not depend on
  // the user's attention. New arrivals while it stays open are read too.
  $effect(() => {
    if ($items.length > 0) {
      void markRead(accountId);
    }
  });

  $effect(() => {
    if ($items.length > 0 && listElement !== null) {
      listElement.scrollTop = listElement.scrollHeight;
    }
  });

  // The soft keyboard shrinks the frame; without this the newest messages end up
  // hidden behind the composer. Only re-pin when the user was at the bottom
  // BEFORE the resize (measured on scroll, since by resize time the shrunken
  // list already reads as scrolled away) — a keyboard must not yank someone
  // away from old messages.
  let wasAtBottom = true;
  $effect(() => {
    const el = listElement;
    if (el === null) {
      return;
    }
    const onScroll = (): void => {
      wasAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
    };
  });
  $effect(() => {
    const viewport = window.visualViewport;
    if (viewport === null || viewport === undefined) {
      return;
    }
    const onResize = (): void => {
      if (wasAtBottom && listElement !== null) {
        requestAnimationFrame(() => {
          if (listElement !== null) {
            listElement.scrollTop = listElement.scrollHeight;
          }
        });
      }
    };
    viewport.addEventListener('resize', onResize);
    return () => {
      viewport.removeEventListener('resize', onResize);
    };
  });

  const liveState = $derived(threadState.states[accountId]);
  const status = $derived($contact?.status ?? 'active');
  const readOnly = $derived(status === 'withdrawn' || (liveState?.readOnly ?? false));
  const paused = $derived($contact?.sendingPaused ?? false);
  const needsReverify = $derived($contact?.needsReverify ?? false);
  const quarantined = $derived(
    $contact?.quarantineUntilMs !== null &&
      $contact?.quarantineUntilMs !== undefined &&
      $contact.quarantineUntilMs > Date.now(),
  );
  const composerDisabled = $derived(readOnly || paused || needsReverify || $contact === undefined);

  const answeredQuestions = $derived.by(() => {
    const out = new Set<string>();
    for (const item of $items) {
      for (const element of item.elements) {
        if (element.type === 'answer') {
          out.add(element.questionId);
        }
      }
    }
    return out;
  });

  /** For each cart_id, the index of its LAST occurrence — older cards go stale. */
  const lastCartIndex = $derived.by(() => {
    const out = new Map<string, number>();
    $items.forEach((item, index) => {
      for (const element of item.elements) {
        if (element.type === 'cart') {
          out.set(element.cartId, index);
        }
      }
    });
    return out;
  });

  function contextFor(item: ConversationItem, index: number): ElementContext {
    const stale = new Set<string>();
    for (const element of item.elements) {
      if (element.type === 'cart' && (lastCartIndex.get(element.cartId) ?? index) > index) {
        stale.add(element.cartId);
      }
    }
    return {
      accountId,
      sequence: sequenceOf(item),
      disabled: composerDisabled,
      answeredQuestions,
      staleCarts: stale,
      mutedCategories: $muted,
      // A control binds to something in the same message. Looking wider would let a
      // card act on a cart the reader is not looking at.
      resolveCart: (cartId) =>
        item.elements.find(
          (e): e is CartElement => e.type === 'cart' && e.cartId === cartId,
        ),
      resolveEvent: (eventId) =>
        item.elements.find(
          (e): e is CalendarEventElement => e.type === 'calendar_event' && e.eventId === eventId,
        ),
      onAnswer: (questionId, text) => {
        void queueMessage({ accountId, elements: [answerElementJson(questionId, text)] });
      },
      onRequestItems: (items) => {
        void queueMessage({ accountId, elements: [requestElementJson(items)] });
      },
      onCartRespond: (cartId, decision, cartItems, note) => {
        void queueCartResponse({
          accountId,
          cartId,
          decision,
          items: cartItems,
          ...(note !== undefined ? { note } : {}),
        });
      },
    };
  }

  function sequenceOf(item: ConversationItem): number {
    // Outbox items have no sequence yet; the calendar current-state guard only
    // needs "not newer than anything stored", which 0 satisfies.
    return item.sendState === undefined ? ($contact?.lastSequence ?? 0) : 0;
  }

  function sendText(text: string): void {
    void queueMessage({ accountId, elements: [textElementJson(text)] });
  }

  async function sendImage(file: File): Promise<void> {
    const prepared = await prepareImage(file);
    void queueMessage({
      accountId,
      elements: [
        imageElementJson({ mediaId: '', width: prepared.width, height: prepared.height }),
      ],
      media: { bytes: prepared.bytes, contentType: prepared.contentType },
    });
  }

  function sendVoice(recording: VoiceRecording): void {
    void queueMessage({
      accountId,
      elements: [
        voiceElementJson({
          mediaId: '',
          durationMs: recording.durationMs,
          waveform: recording.waveform,
        }),
      ],
      media: { bytes: recording.bytes, contentType: recording.contentType },
    });
  }

  const title = $derived(
    $contact === undefined
      ? ''
      : $contact.name.length > 0
        ? $contact.name
        : formatPhone($contact.phone),
  );
</script>

<header class="bar">
  <button class="back" onclick={goBack} aria-label={t('common.back')}>‹</button>
  <button class="title" onclick={() => navigate({ name: 'contact', accountId })}>
    <span class="name">{title}</span>
  </button>
  <!-- One button, always the same glyph, always here. Orders moved inside it: everything
       that lives behind this contact now opens from one place, which keeps the bar the
       app's own and stops it growing as a company publishes more pages. -->
  <button
    class="icon"
    aria-label={t('common.menu')}
    title={t('common.menu')}
    aria-haspopup="dialog"
    aria-expanded={menuOpen}
    onclick={() => (menuOpen = true)}>⋯</button
  >
</header>

{#if menuOpen}
  <ContactMenu {accountId} companyName={title} onclose={() => (menuOpen = false)} />
{/if}

{#if readOnly}
  <Banner kind="warn" text={t('conv.readOnlyBanner')} />
{:else if paused}
  <Banner
    kind="warn"
    text={t('conv.pausedBanner')}
    actionLabel={t('contact.title')}
    onaction={() => navigate({ name: 'contact', accountId })}
  />
{/if}
{#if needsReverify && $contact !== undefined}
  <Banner
    kind="warn"
    text={t('conv.reverifyBanner')}
    actionLabel={t('conv.reverify')}
    onaction={() => navigate({ name: 'add', phone: $contact!.phone })}
  />
{/if}
{#if quarantined}
  <Banner kind="info" text={t('conv.quarantineBanner')} />
{/if}
{#if freshAdd}
  <PushNudge />
{/if}

<div class="list" bind:this={listElement}>
  {#each $items as item, index (item.id)}
    <MessageBubble
      {item}
      ctx={contextFor(item, index)}
      onretry={(uuid) => void retryOutboxItem(uuid)}
      ondiscard={(uuid) => void discardOutboxItem(uuid)}
    />
  {/each}
</div>

<ActivityStrip {accountId} />
<ComposeBar
  disabled={composerDisabled}
  onsendtext={sendText}
  onsendimage={(file) => void sendImage(file)}
  onsendvoice={sendVoice}
/>

<style>
  .bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--line);
    background: var(--surface);
  }
  .back {
    border: 0;
    background: transparent;
    font-size: 28px;
    cursor: pointer;
    color: var(--ink);
    padding: 0 8px;
  }
  .title {
    flex: 1;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    color: var(--ink);
    font: inherit;
    min-width: 0;
  }
  .name {
    font-weight: 700;
    font-size: 17px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
  }
  .icon {
    border: 0;
    background: transparent;
    font-size: 20px;
    cursor: pointer;
    padding: 6px;
  }
  .list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
</style>
